<template>
  <div class="content-panel">
    <div class="panel-title-wrap">
      <h3 class="panel-title">{{ $t("profile.title") }}</h3>
      <span class="panel-sub">{{ $t("profile.subtitle") }}</span>
    </div>
    <div class="profile-form">
      <div class="form-row avatar-row">
        <label class="form-label">{{ $t("profile.avatar") }}</label>
        <div class="avatar-upload">
          <div class="avatar-preview">
            <img
              v-if="form.avatar"
              :src="form.avatar + '?r=' + r"
              class="avatar-img"
              :alt="$t('profile.avatarAlt')"
            />
            <span v-else class="avatar-text-lg">{{ avatarText }}</span>
          </div>
          <button class="btn-upload" @click="handleUploadAvatar">
            {{ $t("profile.changeAvatar") }}
          </button>
          <button class="btn-upload" @click="showAvatarModal = true">
            {{ $t("profile.systemAvatar") }}
          </button>
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="file-input"
            @change="handleFileChange"
          />
        </div>
      </div>
      <div class="form-row">
        <label class="form-label">{{ $t("profile.firstName") }}</label>
        <div class="form-control-wrap">
          <input type="text" v-model="form.first_name" class="form-input" :placeholder="$t('profile.firstNamePlaceholder')" />
        </div>
      </div>
      <div class="form-row">
        <label class="form-label">{{ $t("profile.lastName") }}</label>
        <div class="form-control-wrap">
          <input type="text" v-model="form.last_name" class="form-input" :placeholder="$t('profile.lastNamePlaceholder')" />
        </div>
      </div>
      <div class="form-row">
        <label class="form-label">{{ $t("profile.phone") }}</label>
        <div class="form-control-wrap">
          <input type="text" v-model="form.phone" class="form-input" :placeholder="$t('profile.phonePlaceholder')" />
        </div>
      </div>
      <div class="form-row">
        <label class="form-label">{{ $t("profile.address") }}</label>
        <div class="form-control-wrap">
          <input type="text" v-model="form.address" class="form-input" :placeholder="$t('profile.addressPlaceholder')" />
        </div>
      </div>
      <div class="form-actions">
        <button
          class="btn-submit"
          :disabled="saving"
          @click="handleSaveProfile"
        >
          {{ saving ? $t("profile.saving") : $t("profile.saveInfo") }}
        </button>
      </div>
    </div>

    <!-- 系统头像弹框 -->
    <div
      v-if="showAvatarModal"
      class="avatar-modal-overlay"
      @click.self="showAvatarModal = false"
    >
      <div class="avatar-modal">
        <div class="avatar-modal-header">
          <h3>{{ $t("profile.selectSystemAvatar") }}</h3>
          <button class="avatar-modal-close" @click="showAvatarModal = false">
            ×
          </button>
        </div>
        <div class="avatar-grid">
          <div
            v-for="(avatar, index) in avatarList"
            :key="index"
            class="avatar-grid-item"
            :class="{ active: form.avatar === avatar }"
            @click="selectSystemAvatar(avatar)"
          >
            <img :src="avatar" :alt="$t('profile.avatarAltNum', { count: index + 1 })" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const avatarList = [
  "/statics/avatar/1.jpg",
  "/statics/avatar/2.jpg",
  "/statics/avatar/3.jpg",
  "/statics/avatar/4.jpg",
  "/statics/avatar/5.jpg",
  "/statics/avatar/6.jpg",
  "/statics/avatar/7.jpg",
  "/statics/avatar/8.jpg",
  "/statics/avatar/nan1.jpg",
  "/statics/avatar/nan2.jpg",
  "/statics/avatar/nan3.jpg",
  "/statics/avatar/nan4.jpg",
  "/statics/avatar/nan5.jpg",
  "/statics/avatar/nan6.jpg",
  "/statics/avatar/nv1.jpg",
  "/statics/avatar/nv2.jpg",
  "/statics/avatar/nv3.jpg",
  "/statics/avatar/nv4.jpg",
  "/statics/avatar/nv5.jpg",
  "/statics/avatar/nv6.jpg",
];

export default {
  name: "ProfilePanel",
  props: {
    profile: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      avatarList,
      form: {},
      saving: false,
      // 标记是否上传了新头像
      avatarChanged: false,
      showAvatarModal: false,
      r: new Date().getTime(),
    };
  },

  computed: {
    avatarText() {
      const first = this.form.first_name || "";
      const last = this.form.last_name || "";
      return (first.charAt(0) + last.charAt(0)).toUpperCase() || "TH";
    },
  },
  watch: {
    profile: {
      handler(val) {
        this.form = { ...val };
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    // 点击更换头像，触发隐藏的文件选择框
    handleUploadAvatar() {
      this.$refs.fileInput.click();
    },
    // 选择文件后上传
    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 校验文件类型
      if (!file.type.startsWith("image/")) {
        this.$message.error(this.$t("profile.selectImageFile"));
        return;
      }

      // 校验文件大小（限制 5MB）
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error(this.$t("profile.imageTooLarge"));
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      this.$api.file
        .upload(formData)
        .then(async (res) => {
          if (res.code === 1) {
            this.$message.error(res.msg || this.$t("profile.uploadFail"));
            return;
          }
          // 上传成功，返回 data.path，显示到对应位置
          const path = res.data && res.data.path;
          if (path) {
            this.form.avatar = path;
            // 标记已上传新头像
            this.avatarChanged = true;
            // this.$message.success("头像上传成功");

            //马上更新
            const res = await this.$api.user.updateAvatar({ avatar: path });
            if (res.code === 1) {
              this.$message.error(res.msg || this.$t("profile.updateFail"));
              return;
            }
            this.$message.success(res.msg || this.$t("profile.updateSuccess"));
          } else {
            this.$message.error(this.$t("profile.uploadFailNoPath"));
          }
        })
        .catch(() => {
          this.$message.error(this.$t("profile.uploadFailRetry"));
        })
        .finally(() => {
          // 重置文件输入框，以便再次选择同一文件
          event.target.value = "";
        });
    },
    // 选择系统头像
    async selectSystemAvatar(avatar) {
      this.form.avatar = avatar;
      this.avatarChanged = true;
      this.showAvatarModal = false;
      //保存头像
      const res = await this.$api.user.saveAvatar({ avatar: avatar });
      if (res.code === 1) {
        this.$message.error(res.msg);
        return;
      }
      this.$message.success(res.msg);
    },
    // 保存个人信息（只提交修改的字段）
    handleSaveProfile() {
      if (this.saving) return;
      this.saving = true;

      const params = {
        // 未上传新头像则传空，有修改才传图片路径
        // avatar: this.avatarChanged ? this.form.avatar : "",
        // nickname: this.form.nickname,
        // email: this.form.email,
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        phone: this.form.phone,
        address: this.form.address,
      };

      this.$api.user
        .update(params)
        .then((res) => {
          if (res.code === 1) {
            this.$message.error(res.msg || this.$t("profile.saveFail"));
            return;
          }
          this.$message.success(this.$t("profile.saveSuccess"));
          this.$emit("save", params);
        })
        .catch(() => {
          this.$message.error(this.$t("profile.saveFailRetry"));
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.content-panel {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.panel-title-wrap {
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.panel-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px 0;
}

.panel-sub {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.35);
}

/* ===== 表单行 ===== */
.form-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.form-label {
  width: 100px;
  flex-shrink: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.form-control-wrap {
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
}

.form-input {
  flex: 1;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
  font-family: inherit;
  max-width: 400px;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

.form-input:focus {
  border-color: rgba(0, 180, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(0, 180, 255, 0.05);
}

/* ===== 头像上传 ===== */
.avatar-row {
  padding: 16px 0;
}

.avatar-upload {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-preview {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-text-lg {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.btn-upload {
  padding: 8px 20px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-upload:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.file-input {
  display: none;
}

/* ===== 系统头像弹框 ===== */
.avatar-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

.avatar-modal {
  background: linear-gradient(145deg, #1a2a3a, #0f1a26);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.avatar-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.avatar-modal-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.avatar-modal-close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-family: inherit;
  line-height: 1;
}

.avatar-modal-close:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.avatar-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.avatar-grid-item {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
}

.avatar-grid-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-grid-item:hover {
  transform: scale(1.08);
  border-color: rgba(0, 180, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 180, 255, 0.2);
}

.avatar-grid-item.active {
  border-color: #00b4ff;
  box-shadow: 0 0 0 3px rgba(0, 180, 255, 0.3);
}

.avatar-grid-item.active::after {
  content: "✓";
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #00b4ff;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== 底部提交按钮 ===== */
.form-actions {
  padding-top: 24px;
  display: flex;
  justify-content: flex-start;
}

.btn-submit {
  padding: 12px 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  border: none;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-submit:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 100, 255, 0.3);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px 0;
  }
  .form-label {
    width: 100%;
  }
  .form-control-wrap {
    width: 100%;
    flex-wrap: wrap;
  }
  .form-input {
    max-width: 100%;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .panel-title {
    font-size: 17px;
  }
  .form-actions {
    width: 100%;
  }
  .btn-submit {
    width: 100%;
    justify-content: center;
    text-align: center;
  }
}
</style>
